import { useEffect, useState } from "react";
import Select from "react-select";
import moment from "moment";
import { cn } from "../../../../utils/classnames";
import { SelectDropdownIndicator } from "../../modification-requests/components/Icons";
import SectionTitle from "./SectionTitle";

const EDITABLE_STATUSES = [
  { value: "UNPAID", label: "Unpaid" },
  { value: "PAID", label: "Paid" },
  { value: "CHARGE_PENDING", label: "Charge Pending" },
];

// Compact react-select styling matching the invoice-list filter, sized to sit
// inline inside the info grid.
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "36px",
    borderRadius: "5px",
    borderColor: "#E8E8E8",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": { borderColor: "#E8E8E8" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  singleValue: (base) => ({ ...base, color: "#282828", fontSize: "14px", lineHeight: "22px" }),
  placeholder: (base) => ({ ...base, color: "#7C7B7B", fontSize: "14px", lineHeight: "22px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 10px", color: "#282828" }),
  menu: (base) => ({ ...base, borderRadius: "5px", marginTop: "6px", zIndex: 30 }),
  // Render the menu in a body-level portal so it isn't clipped by the
  // Invoice Information box's overflow-hidden.
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
    color: "#282828",
    cursor: "pointer",
    fontSize: "14px",
    lineHeight: "22px",
    "&:hover": { backgroundColor: "#F7F7F7" },
  }),
};

const periodLabel = (month, year) =>
  moment(`${year}-${String(month).padStart(2, "0")}-01`).locale("en").format("MMMM YYYY");

const Field = ({ label, children, className }) => (
  <div className={className}>
    <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">{label}</div>
    <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{children}</div>
  </div>
);

const InvoiceInformation = ({
  invoice,
  activeCard,
  canCharge,
  charging,
  chargeError,
  onCharge,
  onUpdateStatus,
  updatingStatus,
  statusError,
  periods = [],
  currentInvoiceId,
  onSelectPeriod,
}) => {
  const currentStatus = invoice?.paymentStatus || "UNPAID";
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  const businessName =
    invoice?.user?.businessDetail?.businessInformation?.businessName ||
    invoice?.user?.name ||
    "-";
  const statusDirty = selectedStatus !== currentStatus;

  // Keep the dropdown in sync when the invoice's status changes (e.g. after a
  // successful save or a fresh fetch).
  useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  const selectedStatusOption =
    EDITABLE_STATUSES.find((o) => o.value === selectedStatus) || EDITABLE_STATUSES[0];

  // Period switcher: this account's other invoices. Fall back to just the
  // current period if the list hasn't loaded (or failed).
  let periodOptions = (periods || []).map((p) => ({
    value: p._id,
    label: periodLabel(p.month, p.year),
  }));
  if (!periodOptions.some((o) => o.value === currentInvoiceId)) {
    periodOptions = [
      { value: currentInvoiceId, label: periodLabel(invoice?.month, invoice?.year) },
      ...periodOptions,
    ];
  }
  const selectedPeriodOption =
    periodOptions.find((o) => o.value === currentInvoiceId) || periodOptions[0];

  const showActionRow =
    invoice?.paymentStatus === "UNPAID" ||
    invoice?.paymentStatus === "CHARGE_PENDING" ||
    chargeError;

  return (
    <>
      {/* <SectionTitle title="Invoice Information" /> */}
      <div className="tw:mb-5 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
        <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:p-5 tw:md:grid-cols-3 tw:md:gap-8 tw:lg:grid-cols-5">
          <Field label="Business User">
            <div>{businessName}</div>
            <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">
              {invoice?.user?.email}
            </div>
          </Field>

          <Field label="Period">
            <div className="tw:max-w-[200px]">
              <Select
                value={selectedPeriodOption}
                onChange={(option) => onSelectPeriod && onSelectPeriod(option?.value)}
                options={periodOptions}
                isSearchable={false}
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPlacement="auto"
                components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
              />
            </div>
          </Field>

          <Field label="Status">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:max-w-[200px]">
                <Select
                  value={selectedStatusOption}
                  isDisabled={updatingStatus}
                  onChange={(option) => setSelectedStatus(option?.value)}
                  options={EDITABLE_STATUSES}
                  isSearchable={false}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPlacement="auto"
                  components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
                />
              </div>

              {statusDirty && (
                <div className="tw:flex tw:items-center tw:gap-2">
                  <button
                    type="button"
                    disabled={updatingStatus}
                    onClick={() => onUpdateStatus && onUpdateStatus(selectedStatus)}
                    className="tw:inline-flex tw:h-[36px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border-0 tw:bg-[#3CAEA3] tw:px-4 tw:text-[13px] tw:font-[600] tw:text-white disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                  >
                    {updatingStatus ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    disabled={updatingStatus}
                    onClick={() => setSelectedStatus(currentStatus)}
                    className="tw:inline-flex tw:h-[36px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-4 tw:text-[13px] tw:font-[500] tw:text-[#8D8D8D] disabled:tw:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {invoice?.paymentStatus === "PAID" && invoice?.paidAt && (
                <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">
                  {moment(invoice.paidAt).format("YYYY/MM/DD HH:mm")}
                  {invoice?.paidManually ? " · Marked paid manually" : ""}
                </div>
              )}

              {statusError && (
                <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#C0392B]">
                  {statusError}
                </div>
              )}
            </div>
          </Field>

          <Field label={`Total (${invoice?.totalItem || 0} item${invoice?.totalItem > 1 ? "s" : ""})`}>
            {(invoice?.totalAmount || 0).toLocaleString()} JPY
          </Field>

          <Field label="Payment Method">
            {activeCard ? (
              `${activeCard.brand} ${activeCard.cardNumber}`
            ) : (
              <span className="tw:!text-[#8D8D8D]">No payment method on file</span>
            )}
          </Field>
        </div>

        {showActionRow && (
          <>
            <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />
            <div className="tw:p-5">
              {invoice?.paymentStatus === "UNPAID" && (
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                  <button
                    type="button"
                    disabled={!canCharge || charging}
                    onClick={onCharge}
                    className="tw:border-0 tw:bg-transparent tw:p-0 disabled:tw:cursor-not-allowed"
                  >
                    <span
                      className={cn(
                        "tw:inline-flex tw:h-[45px] tw:min-w-[180px] tw:items-center tw:justify-center tw:rounded-[5px] tw:px-6 tw:font-hanken-grotesk tw:text-[15px] tw:font-[600] tw:leading-[22px] tw:text-white tw:bg-[#3CAEA3]",
                        (!canCharge || charging) && "tw:opacity-50"
                      )}
                    >
                      {charging ? "Charging..." : "Charge Invoice"}
                    </span>
                  </button>
                  {!activeCard && (
                    <span className="tw:font-hanken-grotesk tw:text-[13px] tw:text-[#8D8D8D]">
                      Charging is unavailable — the business user has no active saved payment method.
                    </span>
                  )}
                </div>
              )}

              {invoice?.paymentStatus === "CHARGE_PENDING" && (
                <div className="tw:rounded-[5px] tw:bg-[#FFFAF0] tw:px-4 tw:py-3 tw:font-hanken-grotesk tw:text-[14px] tw:text-[#B0870F]">
                  Charge initiated — the payment confirmation will arrive via the Flywire webhook.
                </div>
              )}

              {chargeError && (
                <div className="tw:mt-3 tw:rounded-[5px] tw:bg-[#FDECEA] tw:px-4 tw:py-3 tw:font-hanken-grotesk tw:text-[14px] tw:text-[#C0392B]">
                  {chargeError}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InvoiceInformation;
